import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { Fragment } from "react"
import { connect } from "react-redux"

import { trackSlide, updateChoice } from "areas/product/store/actions"
import {
  ChoiceOption,
  OptionTitle,
  OptionPrice,
  OptionsSlider,
} from "areas/product/wizard-options/components"
import Image from "components/ui/image"
import { to_currency } from "helpers/number"
import { getPhotosDesc } from "helpers/product/description-details"
import { imageLookup } from "helpers/product/image-lookup"
import { filterProductOptions } from "helpers/product/product-filtering"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

const ChoiceOptions = ({
  selectedMetalFilter,
  colorSelection,
  data,
  selectedValue,
  setChoice,
}) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  // Example for data.image_size: JWLN0300 Name Thickness (o1)
  let flexBasis = "28%"
  let tabletFlexBasis = colorSelection ? "78px" : "100px"
  if (data.image_size) {
    try {
      const parsedImageSize = JSON.parse(data.image_size)
      flexBasis = parsedImageSize._
      tabletFlexBasis = parsedImageSize.tablet
    } catch (err) {
      flexBasis = tabletFlexBasis = data.image_size
    }
  }

  return (
    <OptionsSlider
      aria-label={`${data.heading.title} Options`}
      className={cn(
        "tablet:px-0 px-12",
        colorSelection ? "tablet:justify-center" : "tablet:justify-start"
      )}
      role="radiogroup"
      style={{
        "--flex-basis": flexBasis,
        "--tablet-flex-basis": tabletFlexBasis,
      }}
    >
      {filterProductOptions(data.options, selectedMetalFilter, true).map(
        (opt, i) => {
          const selected = selectedValue === opt.value
          return (
            <ChoiceOption
              aria-checked={selected}
              className="tablet:basis-(--tablet-flex-basis) basis-(--flex-basis) min-w-0"
              data-cy={`product-wizard-option-${data.pref}-${opt.value}`}
              key={`imgopt-${opt.value}`}
              onClick={() => setChoice(opt)}
              role="radio"
              tabIndex={selected ? 0 : -1}
              {...keyboardProps}
            >
              {!!opt.thumb && (
                <Image
                  alt={opt.description}
                  className="w-full"
                  height="130"
                  src={imageLookup("choice", opt.thumb, "simple")}
                  width="130"
                />
              )}
              <OptionTitle>
                {opt.display_name.split("\n").map((line, j) => (
                  <Fragment key={`icon-label-${data.pref}-${i}-${j}`}>
                    {line}
                    <br />
                  </Fragment>
                ))}
              </OptionTitle>
              {opt.description && <OptionTitle>{opt.description}</OptionTitle>}
              <OptionPrice>
                {opt.price > 0 ? to_currency(opt.price) : ""}
              </OptionPrice>
            </ChoiceOption>
          )
        }
      )}
    </OptionsSlider>
  )
}

ChoiceOptions.propTypes = {
  colorSelection: PropTypes.bool,
  data: PropTypes.object,
  selectedMetalFilter: PropTypes.object,
  selectedValue: PropTypes.string,
  setChoice: PropTypes.func,
}
/**
 * Choice option in image view
 * example product - JWLM0069-7
 */
const InternalImageOptions = ({
  data,
  selectedMetalFilter,
  selectedOption,
  selectedValue,
  setChoice,
}) => {
  const colorSelection = data.color_selection

  return (
    <>
      <FocusScope>
        <ChoiceOptions
          colorSelection={colorSelection}
          data={data}
          selectedMetalFilter={selectedMetalFilter}
          selectedValue={selectedValue}
          setChoice={setChoice}
        />
      </FocusScope>
      {selectedOption?.description_content &&
        selectedOption?.description_title && (
          <div className="desktop:mb-14 desktop:px-0 mb-8 px-14">
            <p className="text-primary text-14 leading-22 mb-6 font-medium tracking-tight">
              {selectedOption?.description_title}
            </p>
            <p className="text-12 leading-14 font-medium">
              {selectedOption?.description_content}
            </p>
          </div>
        )}
      {/* e.g. JWLP0660 */}
      {data.title === "Photos" &&
        ["Paper", "Metal"].includes(selectedValue) && (
          <div className="desktop:mb-14 desktop:px-0 mb-8 px-14">
            <p className="text-primary text-14 leading-22 mb-6 font-medium tracking-tight">
              About {selectedValue} Photos
            </p>
            <p className="text-12 leading-14 font-medium">
              {getPhotosDesc(selectedValue)}
            </p>
          </div>
        )}
    </>
  )
}

const mapStateToProps = (state, ownProps) => {
  let selectedMetalFilter = ""
  if (typeof state.choices.sku !== "undefined") {
    selectedMetalFilter = { pref: "sku", value: state.choices.sku.value }
  }

  let selectedOption = null
  let selectedValue = ""
  if (state.choices[ownProps.data.pref]) {
    selectedValue = state.choices[ownProps.data.pref].value
    selectedOption = filterProductOptions(
      ownProps.data.options,
      selectedMetalFilter,
      true
    ).find((item) => item.value === selectedValue)
  }

  return {
    selectedMetalFilter,
    selectedOption,
    selectedValue,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    setChoice: (data) => {
      dispatch(updateChoice(ownProps.data.pref, data, true))
      if (data.track_slide) {
        dispatch(trackSlide(data.track_slide))
      }
    },
  }
}

InternalImageOptions.propTypes = {
  data: PropTypes.object,
  selectedMetalFilter: PropTypes.object,
  selectedOption: PropTypes.object,
  selectedValue: PropTypes.string,
  setChoice: PropTypes.func,
}

const ImageOptions = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalImageOptions)

export default ImageOptions
